import type { TouchEventHandler } from 'react'
import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { sendTrackingRequestAction } from 'client/contexts/Tracking/actions'
import { clickZoomCarouselDesktop } from 'client/contexts/Tracking/events'
import { useProperty } from 'src/hooks/useProperty'
import { useDevice } from 'src/hooks/useDevice'
import Modal from 'client/components/Modal'
import PhotosGallery from 'src/comps/Molecules/PhotosGallery'
import { getPropertyPricing, getIsPrimeBestPrice } from 'src/effects/Availability/selectors'

import MainImage from './MainImage'
import { ZoomCTA } from '../Molecules/ZoomCTA'
import { BreakpointSizes } from 'src/types/device'
import { useKafkaEvent } from 'src/hooks/useKafkaEvent'
import { getSearchPaxes } from 'src/effects/Search/selectors'
import { getLocation } from 'client/contexts/Location/selectors'
import { MAX_ALLOWED_THUMBNAILS, thumbnailImageSizes } from './constants'
import { ShowMoreThumbnail } from './ShowMoreThumbnail'

import styles from './style.module.css'
import { Image, PromotionTag } from '@s4l/uikit'
import { Typography } from '@s4l/ds'
import { useS4LContext } from '@s4l/react-context'
import { getResponsiveImgProps, getThumbnailsToShow } from './utils'
import { Layout } from './layout'
import { calculateDiscount } from 'client/utils'

interface Props {
  onPriceClick: () => void
  onVisibilityChange?: (isVisible: boolean) => void
}

const Gallery = ({ onPriceClick, onVisibilityChange }: Props) => {
  const { i18n } = useS4LContext()
  const dispatch = useDispatch()
  const { sendEvent: sendKafkaClickHotelGallery } = useKafkaEvent({ page: 'hotel_details' })

  const occupancies = useSelector(getSearchPaxes)
  const locationState = useSelector(getLocation)

  const { activeBreakpoint } = useDevice()
  const property = useProperty()
  const { photos, name, stars } = property

  const { initialBestPrice, totalDiscount } = useSelector(getPropertyPricing)

  const isPrime = useSelector(getIsPrimeBestPrice)

  const isMobile = activeBreakpoint < BreakpointSizes.M
  const isTablet = activeBreakpoint >= BreakpointSizes.M && activeBreakpoint < BreakpointSizes.L

  const [currentSlide, setCurrentSlide] = useState(0)
  const [showCarousel, setShowCarousel] = useState(false)
  const [slideX, setSlideX] = useState(0)
  const [timer, setTimer] = useState<number>()

  useEffect(() => {
    onVisibilityChange?.(showCarousel)
  }, [showCarousel, onVisibilityChange])

  useEffect(() => {
    return () => {
      if (!timer) return
      clearTimeout(timer)
    }
  }, [timer])

  function handleTracking() {
    dispatch(sendTrackingRequestAction({ event: clickZoomCarouselDesktop() }))
    sendKafkaClickHotelGallery(
      { section: 'hotel_images' },
      {
        occupancies,
        search: {
          country: locationState?.country,
          type: locationState?.citySlug ? 'city' : 'zone',
          slug: locationState?.slug,
        },
      }
    )
  }

  const handleClick = (index = 0) => {
    setCurrentSlide(index)
    setShowCarousel(true)
    handleTracking()
  }

  const closeModal = () => {
    setShowCarousel(false)
  }

  const closeModalAndScroll = () => {
    closeModal()
    const timerClick = window.setTimeout(() => {
      onPriceClick()
    }, 0)
    setTimer(timerClick)
  }

  const initSlide = currentSlide === 0
  const lastSlide = currentSlide === photos.length - 1

  const startSlide: TouchEventHandler<HTMLDivElement> = event => {
    setSlideX(event.changedTouches[0].clientX)
  }

  function prevSlide() {
    return !initSlide && setCurrentSlide(currentSlide - 1)
  }

  function nextSlide() {
    return !lastSlide && setCurrentSlide(currentSlide + 1)
  }

  const changeSlide: TouchEventHandler<HTMLDivElement> = event => {
    const slideDiff = event.changedTouches[0].clientX - slideX
    const direction = Math.sign(slideDiff)

    if (direction < 0) nextSlide()
    if (direction > 0) prevSlide()

    setSlideX(0)
  }

  const pointerEvents = {
    onTouchEnd: changeSlide,
    onTouchStart: startSlide,
  }

  const discount = calculateDiscount(initialBestPrice, totalDiscount)

  const showMoreThumbnail = photos[MAX_ALLOWED_THUMBNAILS + 1]
  const thumbnailsToShow = getThumbnailsToShow(isMobile, isTablet, photos)

  return (
    <div className={styles.galleryContainer}>
      {discount && (
        <PromotionTag
          className={['is-absolute', 'top-16']}
          discount={discount}
          prime={isPrime}
          variant="rounded"
        />
      )}
      <Layout>
        <MainImage handleClick={handleClick} pointerEvents={pointerEvents} photos={photos} />

        {thumbnailsToShow.map((img, index: number) => {
          const imageProps = getResponsiveImgProps(img, thumbnailImageSizes)
          return (
            <div
              className={styles.thumbnail}
              key={img.path}
              data-testid="gallery-thumbnail"
              {...pointerEvents}
            >
              <Image
                alt={`thumbnail-${index}`}
                onClick={() => (isMobile ? handleClick(index) : handleClick(index + 1))}
                {...imageProps}
                loading={index === 0 ? 'eager' : 'lazy'}
                preload={index === 0}
              />
            </div>
          )
        })}

        {showMoreThumbnail ? (
          <ShowMoreThumbnail
            handleClick={handleClick}
            image={showMoreThumbnail}
            photosCount={photos.length - 1}
          />
        ) : (
          <button type="button" className={styles.showMore} onClick={() => handleClick()}>
            <Typography variant="body" size="m">
              {i18n.formatMessage({ id: 'detail.show-all-photos' })}
            </Typography>
          </button>
        )}
      </Layout>

      <Modal
        title={
          <div className={styles.galleryTitle}>
            {i18n.formatMessage({ id: 'global.hotel-images' })}
          </div>
        }
        show={showCarousel}
        variant="fullscreen"
        handleClose={closeModal}
        displayHeader
        displayHeaderShadow
      >
        <PhotosGallery
          focusOnImageIndex={currentSlide}
          photos={photos}
          CTA={<ZoomCTA name={name} stars={stars} onClick={closeModalAndScroll} />}
        />
      </Modal>
    </div>
  )
}

export default Gallery
